/**
 * Panel aggregations for the sales dashboard F2 (VIB-1100..1103). Builds on the
 * F0 primitives in dashboard-bf-calc (bfLevert, deriveBfTarget, feilbudsjettering,
 * pipelineBf) — never recomputes BF. Pure; period/now passed in.
 *
 *  - F2.1 person leaderboard      → calcPersonLeaderboard
 *  - F2.2 per-seller budget acc.  → calcSellerBudgetAccuracy
 *  - F2.3 best / worst projects   → calcTopProjectsByBf / calcWorstBudgetProjects
 *  - F2.4 pipeline per person     → calcPipelinePerPerson
 */
import {
  type DashboardProject,
  type DashboardOwner,
  type BfTargetSalesUnit,
  bfLevert,
  deriveBfTarget,
  feilbudsjettering,
  pipelineBf,
} from "./dashboard-bf-calc";
import { type DashboardPeriod, periodContains } from "./dashboard-period";
import { isSalesProject } from "./offer-closed-date";

const numOf = (v: number | null) => Number(v ?? 0);

/** Project ids a person owns (via project_owners). */
function ownedProjectIds(owners: DashboardOwner[], userId: string): Set<string> {
  return new Set(owners.filter((o) => o.user_id === userId).map((o) => o.project_id));
}

// ── F2.1 Person leaderboard ──────────────────────────────────────────────

export interface LeaderboardEntry {
  userId: string;
  bfLevertKr: number;
  /** Enhetens FULLE periodemål (aldri delt på medlemsantall — VIB-1285). */
  targetKr: number;
  /** Personens BIDRAG som andel av enhetsmålet — ikke individuell måloppnåelse. */
  achievementPct: number | null;
}

/**
 * Per-person delivered BF, sorted best first. Reuses bfLevert + deriveBfTarget
 * so attribution/exclusions match the KPI cards. Pct er bidragsandel av
 * enhetens felles mål (målsemantikk låst 16.07, VIB-1285) — oppnådd-status
 * finnes kun på enhetsnivå (`unitGoalStatus`).
 */
export function calcPersonLeaderboard(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  salesUnits: BfTargetSalesUnit[],
  period: DashboardPeriod,
  userIds: string[],
): LeaderboardEntry[] {
  const rows = userIds.map((userId) => {
    const bfLevertKr = bfLevert(projects, owners, period, { userId });
    const targetKr = deriveBfTarget({ salesUnits, period, userId });
    const achievementPct = targetKr > 0 ? Math.round((bfLevertKr / targetKr) * 1000) / 10 : null;
    return { userId, bfLevertKr, targetKr, achievementPct };
  });
  return rows.sort((a, b) => b.bfLevertKr - a.bfLevertKr);
}

// ── F2.2 Per-seller budget accuracy (+ trend) ─────────────────────────────

export interface SellerAccuracyEntry {
  userId: string;
  bfEstimatedKr: number;
  bfActualKr: number;
  deviationKr: number;
  deviationPct: number | null;
  projectCount: number;
  /** Same metric for the previous period, for trend display. */
  prevDeviationPct: number | null;
}

/**
 * Per-seller weighted budget deviation for the period, with the previous
 * period's deviation for trend. Only sellers with completed projects in the
 * current period are returned, sorted by smallest |deviation| (most precise
 * first — rewards precise budgets).
 */
export function calcSellerBudgetAccuracy(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  prevPeriod: DashboardPeriod,
  userIds: string[],
): SellerAccuracyEntry[] {
  const rows = userIds
    .map((userId) => {
      const cur = feilbudsjettering(projects, owners, period, { userId });
      const prev = feilbudsjettering(projects, owners, prevPeriod, { userId });
      return {
        userId,
        bfEstimatedKr: cur.bfEstimatedKr,
        bfActualKr: cur.bfActualKr,
        deviationKr: cur.deviationKr,
        deviationPct: cur.deviationPct,
        projectCount: cur.projectCount,
        prevDeviationPct: prev.deviationPct,
      };
    })
    .filter((r) => r.projectCount > 0);
  return rows.sort((a, b) => Math.abs(a.deviationPct ?? 0) - Math.abs(b.deviationPct ?? 0));
}

// ── F2.3 Best / worst projects ───────────────────────────────────────────

export interface ProjectBfEntry {
  projectId: string;
  bfActualKr: number;
}

export interface ProjectDeviationEntry {
  projectId: string;
  bfEstimatedKr: number;
  bfActualKr: number;
  deviationKr: number;
  deviationPct: number;
}

/** Sales projects completed in the period, optionally limited to a person's. */
function completedInPeriod(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  userId: string | null | undefined,
): DashboardProject[] {
  const owned = userId ? ownedProjectIds(owners, userId) : null;
  return projects.filter(
    (p) =>
      isSalesProject(p) &&
      p.project_status === "completed" &&
      p.completed_at != null &&
      periodContains(period, p.completed_at) &&
      (!owned || owned.has(p.id)),
  );
}

/** Top-N projects by realised BF (VIB-1102). */
export function calcTopProjectsByBf(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  opts: { userId?: string | null; limit?: number } = {},
): ProjectBfEntry[] {
  const limit = opts.limit ?? 5;
  return completedInPeriod(projects, owners, period, opts.userId)
    .map((p) => ({ projectId: p.id, bfActualKr: Math.round(numOf(p.bf_actual)) }))
    .sort((a, b) => b.bfActualKr - a.bfActualKr)
    .slice(0, limit);
}

/** Top-N projects by absolute budget deviation, worst first (VIB-1102). */
export function calcWorstBudgetProjects(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  period: DashboardPeriod,
  opts: { userId?: string | null; limit?: number } = {},
): ProjectDeviationEntry[] {
  const limit = opts.limit ?? 5;
  return completedInPeriod(projects, owners, period, opts.userId)
    .filter((p) => numOf(p.bf_estimated) > 0)
    .map((p) => {
      const bfEstimatedKr = Math.round(numOf(p.bf_estimated));
      const bfActualKr = Math.round(numOf(p.bf_actual));
      const deviationKr = bfActualKr - bfEstimatedKr;
      return {
        projectId: p.id,
        bfEstimatedKr,
        bfActualKr,
        deviationKr,
        deviationPct: Math.round((deviationKr / bfEstimatedKr) * 1000) / 10,
      };
    })
    .sort((a, b) => Math.abs(b.deviationPct) - Math.abs(a.deviationPct))
    .slice(0, limit);
}

// ── F2.4 Pipeline per person ─────────────────────────────────────────────

export interface PipelineEntry {
  userId: string;
  pipelineKr: number;
}

/** Per-person pipeline BF (accepted, not completed), sorted desc, non-zero. */
export function calcPipelinePerPerson(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  userIds: string[],
): PipelineEntry[] {
  return userIds
    .map((userId) => ({ userId, pipelineKr: pipelineBf(projects, owners, { userId }) }))
    .filter((r) => r.pipelineKr !== 0)
    .sort((a, b) => b.pipelineKr - a.pipelineKr);
}
